import { CountryProfile, CountryCode } from '@/store/ui';
import { CAPaymentProviders, UKPaymentProviders, USPaymentProviders, countries } from '@/constants';
import { CmsLink, CustomerDeliveryAddress, Restaurant, OrderWithContext, CustomerProfile } from '@/models';
import { AppState } from '@/store';
import { OrderService } from '@/services/order/order.service';
import { ConfigService } from '@/services/config/config.service';
import { UiService } from '@/services/ui/ui.service';
import { getI18nInstance } from '@/i18n';
import { Config } from '@/environment';

export interface PersistCrossDomainDataOptions {
  data: PersistCrossDomainData;
  restaurant: Restaurant | null;
  sessionFrameUrl: string;
  shouldRedirect: boolean;
  redirectQueryParams?: { [key: string]: string };
}

export interface PersistCrossDomainData {
  order?: OrderWithContext | null;
  deliveryAddress?: CustomerDeliveryAddress | null;
  jwt: string;
}

export interface CountryNameOptions {
  useShortName?: boolean | undefined;
  includePrefix?: boolean | undefined;
}

export function getCountryByHostname(hostname: string): CountryProfile {
  const topLevelDomain = hostname.substring(hostname.lastIndexOf('.') + 1);
  let countryCode = 'us';

  switch (topLevelDomain.toLowerCase()) {
    case 'uk':
      countryCode = 'gb';
      break;
    case 'ca':
      countryCode = 'ca';
      break;
    default:
      countryCode = 'us';
  }

  return countries[countryCode] || countries.us;
}

export function getCmsBaseUrlByCountryCode(config: Config, countryCode: string): string {
  switch (countryCode) {
    case CountryCode.GB:
      return config.cms.baseUrlUk;
    case CountryCode.CA:
      return config.cms.baseUrlCanada;
    case CountryCode.US:
    default:
      return config.cms.baseUrl;
  }
}

export function updateBrowserUrl(location: any, newTopLevelDomain: string, queryParams?: { [key: string]: string }): void {
  const protocol = location.protocol;
  const hostname = location.hostname;
  const newHostName = hostname.substring(0, hostname.indexOf('chipotle') + 8);
  const port = location.port ? ':' + location.port : '';

  let fromFac = false;
  let queryString = '';
  if (queryParams) {
    queryString = '?';
    Object.keys(queryParams).forEach(key => {
      queryString += `${key}=${queryParams[key]}&`;

      if (key == 'fromFac' && queryParams[key] == 'true') {
        fromFac = true;
      }
    });
  }

  // Always redirect to the Order page if coming from FAC pill, otherwise go to the root
  const orderPageDestination = fromFac ? '/order/' : '';
  
  // Construct the base URL with protocol and hostname
  let baseUrl = `${protocol}//${newHostName}`;

  // Append the top-level domain if the hostname is not 'localhost'
  if (newHostName !== 'localhost') {
    baseUrl += newTopLevelDomain;
  }
  
  // Append the port and specific page destination
  const fullUrl = `${baseUrl}${port}${orderPageDestination}`;
  
  // Finally, append the query string if it exists
  const newUrl = `${fullUrl}${queryString}`;
  location.replace(newUrl);
}

export function getTopLevelDomainByCountryCode(countryCode: string): string {
  return countries[countryCode.toLowerCase()]?.topLevelDomain ?? '.com';
}

export function getCountryCodeByTopLevelDomain(hostName: string = window.location.hostname): string {
  return getCountryByHostname(hostName).countryCode;
}

export function getCountryName(countryCode: string, includePrefix: boolean = false): string {
  const country = countries[countryCode.toLocaleLowerCase()];
  if (!country) {
    return '';
  }
  const countryName = country.shortName;
  const prefix = includePrefix ? country.namePrefix + ' ' : '';

  return `${prefix}${countryName}`;
}

export function parseCountryCode(value: string, defaultValue: CountryCode = CountryCode.US): CountryCode {
  const refinedValue = value.trim().toUpperCase();
  return refinedValue in CountryCode ? (refinedValue as CountryCode) : defaultValue;
}

export function persistCrossDomainData(options: PersistCrossDomainDataOptions | null) {
  if (!options) {
    return;
  }

  const { data, shouldRedirect, restaurant, sessionFrameUrl, redirectQueryParams } = options;

  const iframe = document.getElementById('sessionFrame') as unknown as HTMLIFrameElement;
  if (iframe) {
    // Send a message to the iframe to store data
    const win = iframe.contentWindow;
    if (win) {
      if (shouldRedirect) {
        const messageHandler = (event: any) => {
          if (event.origin !== sessionFrameUrl) {
            return;
          }

          if (event.data && JSON.parse(event.data)) {
            window.removeEventListener('message', messageHandler);
            updateBrowserUrl(location, getTopLevelDomainByCountryCode((restaurant && restaurant.address.countryCode) || ''), redirectQueryParams);
          }
        };

        // overwrite onmessage set from cmg-app.ts onLoadSessionIFrame so it does not delete the data after the read
        window.onmessage = messageHandler;
      }

      // first save the customer data, then send a message to get it to trigger the redirect in order to ensure it was saved
      win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'set', data }), sessionFrameUrl);
      if (shouldRedirect) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'get' }), sessionFrameUrl);
      }
    }
  }
}

export function shouldSwitchDomain(currentRestaurant: Restaurant | null): boolean {
  const countryCode = getCountryCodeByTopLevelDomain();
  if (currentRestaurant && countryCode != currentRestaurant.address.countryCode) {
    return true;
  }

  return false;
}

export function startSwitchDomainProcess(state: AppState, uiService: UiService, orderService: OrderService, configService: ConfigService) {
  
  const shouldShowCountrySwitchAlertModal = showCountrySwitchAlertModal(state);

  persistCrossDomainData(getPersistCrossDomainDataOptions(state, orderService, configService, shouldShowCountrySwitchAlertModal));

  if (shouldShowCountrySwitchAlertModal) {
    uiService.setModalVisibility('countrySwitchAlertModal', true);
    uiService.firstFocus();
  } else {
    const tld = getTopLevelDomainByCountryCode(GetCountryCode(state, orderService));
    const redirectQueryParams = getRedirectParams(state, orderService);
    updateBrowserUrl(location, tld, redirectQueryParams);
  }
}

export function getRedirectParams(state: AppState, orderService: OrderService) {
  const restaurant = state.restaurant.selectedRestaurant;
  const order = orderService.getCurrentOrder();
  const redirectQueryParams: { [key: string]: string } = {};

  if (order) {
    redirectQueryParams.hasBag = 'true';
  }

  if (orderService.isPickingUp() && restaurant) {
    redirectQueryParams.restaurant = restaurant.restaurantNumber.toString();
    redirectQueryParams.hideToast = 'true';
    redirectQueryParams.fromFac = 'true';
  }
  return redirectQueryParams;
}

export function getPersistCrossDomainDataOptions(state: AppState, orderService: OrderService, configService: ConfigService, shouldShowCountrySwitchAlertModal: boolean | null ): PersistCrossDomainDataOptions | null {
  if (!state.restaurant.selectedRestaurant) {
    return null;
  }

  const order = orderService.getCurrentOrder();
  const redirectQueryParams: { [key: string]: string } = {};
  redirectQueryParams.fromFac = 'true';

  if (orderService.isPickingUp()) {
    redirectQueryParams.restaurant = state.restaurant.selectedRestaurant.restaurantNumber.toString();
    if (shouldShowCountrySwitchAlertModal) {
      redirectQueryParams.hideToast = 'true';
    }
  } else {
    if (!shouldShowCountrySwitchAlertModal) {
      redirectQueryParams.showDeliveryToast = 'true';
    }
  }

  if (order) {
    redirectQueryParams.hasBag = 'true';
  }

  return {
    data: {
      deliveryAddress: state.order.selectedDeliveryAddress,
      order,
      jwt: state.customer.jwt,
    },
    sessionFrameUrl: configService.getSessionFrameUrl(),
    restaurant: state.restaurant.selectedRestaurant,
    shouldRedirect: shouldShowCountrySwitchAlertModal ? false : true,
    redirectQueryParams,
  };
}

function GetCountryCode(state: AppState, orderService: OrderService) :string {  
    return orderService.isPickingUp()
    ? state.restaurant.selectedRestaurant!.address.countryCode!
    : state.order.selectedDeliveryAddress!.countryCode!
}

function showCountrySwitchAlertModal(state: AppState) {
  
  const selectedRestaurant = state.restaurant.selectedRestaurant;
  const tldCountryCode = getCountryCodeByTopLevelDomain();
  const doesRestaurantCountryMatchDomain = tldCountryCode === selectedRestaurant?.address.countryCode;
  const isCustomerAuthenticated = !!state.customer.profile && !state.customer.profile?.isGuest;
  const isUKInvolved = tldCountryCode === CountryCode.GB || selectedRestaurant?.address.countryCode === CountryCode.GB;
  
  return !doesRestaurantCountryMatchDomain && isCustomerAuthenticated && !isUKInvolved;
}

export function shouldShowLink(siteCountryCode: string, isCustomerLoggedIn: boolean, state?: AppState) {
  return (link: CmsLink) => {
    const linkText = link.type == 'text' ? link.text : '';
    const shouldShow: { [key: string]: boolean } = {
      Menu: true,
      Order: true,
      Catering: state?.featureFlags.orderingStatus?.isCateringEnabled ?? true,
      Rewards: (state?.featureFlags.orderingStatus?.isRewardsOnline ?? true) && (isCustomerLoggedIn || isLoyaltyEnabled(siteCountryCode)),
      Offers: !shouldShowRewards(siteCountryCode),
      'Our Values': true,
      Nutrition: true,
    };
    return shouldShow[linkText];
  };
}

export function shouldShowRewards(countryCode: string): boolean {
  return isLoyaltyEnabled(countryCode);
}

export function isLoyaltyEnabled(countryCode: string): boolean {
  return countries[countryCode.toLowerCase()]?.isLoyaltyEnabled || false;
}

export function isUserLoyaltyEligible(siteCountryCode: string, userProfileCountry: string): boolean {
  return isLoyaltyEnabled(siteCountryCode) && userProfileCountry === siteCountryCode;
}

export function isSustainabilityEnabled(countryCode: string): boolean {
  return countries[countryCode.toLowerCase()]?.isSustainablityEnabled || false;
}

// This is to check if user is signed in as a Canadian or a guest that is in Canada.
export function isCustomerCanadian(customer: CustomerProfile | null, state: AppState): boolean | null {
  return (customer && customer.country == CountryCode.CA) || state.ui.countryProfile.countryCode == CountryCode.CA;
}

export function isAutoSubscribesToLoyaltyEmail(countryCode?: string): boolean {
  return countries[(countryCode ?? '').toLowerCase()]?.isAutoSubscribesToLoyaltyEmail ?? false;
}

export function isAutoSubscribesToMarketingEmail(countryCode?: string): boolean {
  return countries[(countryCode ?? '').toLowerCase()]?.isAutoSubscribesToMarketingEmail ?? false;
}

export function isCardPaymentProviderInCountry(countryCode: string, paymentProviderId: string): boolean {
  switch (countryCode.toLocaleLowerCase()) {
    case 'us':
      return USPaymentProviders.includes(paymentProviderId);
    case 'ca':
      return CAPaymentProviders.includes(paymentProviderId);
    case 'uk':
    case 'gb':
      return UKPaymentProviders.includes(paymentProviderId);
    default:
      return false;
  }
}

export function countryNeedsBirthdate(countryCode: string): boolean {
  switch (countryCode) {
    case CountryCode.CA:
      return false;
    case CountryCode.US:
      return true;
    default:
      return true;
  }
}

export function getFeatureFlagDefaults(countryCode: string): any {
  const country = countries[countryCode.toLowerCase()];

  if (country) {
    return {
      isDeliveryEnabled: country.facOptions.isDeliveryEnabled,
      isCateringEnabled: country.isCateringEnabled,
      isRewardsOnline: country.bagOptions.isRewardsOnline,
      isDonationsEnabled: country.isDonationsEnabled,
      isGiftCardsEnabled: country.isGiftCardsEnabled,
      isPepperChatEnabled: country.isPepperEnabled,
      isFeedbackSurveyEnabled: country.isFeedbackSurveyEnabled,
      isFoodprintEnabled: country.isFoodprintEnabled,
      isAlternatePickupEnabled: country.isAlternatePickupEnabled,
      aboveStorePaymentOnline: country.paymentOptions.aboveStorePaymentOnline,
      isChasePaymentOnline: country.paymentOptions.isChasePaymentOnline,
      isChaseUKPaymentOnline: country.paymentOptions.isChaseUKPaymentOnline,
      isUComPaymentOnline: country.paymentOptions.isUComPaymentOnline,
    };
  }

  return {};
}

// Returns ISO-4217 currency code
export function getCurrencyCode(countryCode: string): string {
  switch(countryCode) {
    case CountryCode.US:
      return 'USD';
    case CountryCode.GB:
      return 'GBP';
    case CountryCode.CA:
      return 'CAD';
    default:
      return '';
  }
}
